/**
 * @fileoverview Classes whose remaining meetings were set in another time
 * zone than their own (GET /org/schedule-zones), and the way to move them.
 *
 * A class meets in its program address's zone, else the organization's
 * (services/schedule_zones). One set up before either was known took the zone
 * of whatever computer set it up, and its meetings still read in that zone:
 * the calendar shows the time as entered, but reminders and Kalver Scheduler
 * go by the zone. Moving keeps every time as entered (4:00 PM stays 4:00 PM)
 * and changes only the zone it is read in; meetings already past stay as they
 * happened.
 *
 * Renders nothing while there is nothing to move, so it can sit under any
 * card that changes a zone. `programId` narrows it to one program; `revision`
 * re-reads it after that card saves.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, Icon } from "@/components/studio/primitives";

const zoneName = (zone) => String(zone || "").replaceAll("_", " ");
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "es"}`;

/** "1 class is skipped: its times fall in a clock change." and the like. */
const SKIPPED = {
  clock_change: "a meeting time falls in a clock change there",
  changed: "its schedule changed meanwhile",
  nothing_to_move: "nothing left to move",
  not_movable: "its meetings are managed elsewhere",
  not_found: "it could not be found",
};

export default function ScheduleZoneCheck({ programId = null, revision = 0, testId = "schedule-zone-check" }) {
  const [rows, setRows] = useState(null);
  const [moving, setMoving] = useState(false);

  const load = useCallback(() => {
    orgApi
      .get("/org/schedule-zones", programId ? { params: { program_id: programId } } : undefined)
      // Rows without their zones (an older server, a stray reply) are not ours to list.
      .then((res) => setRows((res.data?.classes || []).filter((r) => Array.isArray(r.from) && r.to)))
      .catch(() => setRows([]));
  }, [programId]);

  useEffect(() => {
    load();
  }, [load, revision]);

  if (!rows || rows.length === 0) return null;

  const meetings = rows.reduce((n, r) => n + r.meetings, 0);
  const move = async () => {
    setMoving(true);
    try {
      const res = await orgApi.post("/org/schedule-zones/move", { class_ids: rows.map((r) => r.class_id) });
      const moved = res.data?.moved || [];
      const skipped = res.data?.skipped || [];
      if (moved.length) toast.success(`${plural(moved.length, "class")} moved to ${zoneName(moved[0].to)}`);
      if (skipped.length) {
        toast.error(`${plural(skipped.length, "class")} not moved`, {
          description: [...new Set(skipped.map((s) => SKIPPED[s.reason] || s.reason))].join("; "),
        });
      }
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't move those classes"));
    } finally {
      setMoving(false);
    }
  };

  return (
    <Card className="mt-4" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--state-warning-border)] bg-[var(--state-warning-dim)]">
          <Icon name="clock" size={16} className="text-[var(--state-warning)]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">
            {plural(rows.length, "class")} set up in another time zone
          </div>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            Their remaining meetings read in a different zone from their program&rsquo;s. Moving them keeps every time
            as entered (4:00 PM stays 4:00 PM) and fixes reminders and calendar copies; past meetings stay as they
            happened.
          </p>
          <ul className="mt-3 space-y-1" data-testid={`${testId}-list`}>
            {rows.map((r) => (
              <li key={r.class_id} className="font-sans text-[13px] text-[var(--text-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">{r.class_name}</span>
                {!programId && <span className="text-[var(--text-tertiary)]"> · {r.program_title}</span>}
                <span className="text-[var(--text-tertiary)]">
                  {" "}
                  · {r.from.map(zoneName).join(", ")} → {zoneName(r.to)} · {r.meetings} meeting{r.meetings === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
          <Button className="mt-3" onPress={move} disabled={moving} data-testid={`${testId}-move`}>
            {moving ? "Moving…" : `Move ${meetings} meeting${meetings === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </Card>
  );
}
